import React, { useState } from 'react';
import { 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { API } from 'aws-amplify';
import { updateVideoData as updateVideoMutation } from '../../graphql/mutations';

/**
 * CrossStartSetter Component
 * 
 * Allows users to set the crossStart time by clicking on the displayed value
 * and using the current video timestamp.
 * 
 * @param {Object} props
 * @param {string} props.videoId - VideoData record ID
 * @param {number} props.crossStart - Current crossStart value in seconds
 * @param {number} props.currentVideoTime - Current video playback time in seconds
 * @param {Object} props.rawVideoData - Raw VideoData object (needed for _version)
 * @param {function} props.onCrossStartChange - Callback when crossStart is updated
 */
function CrossStartSetter({ 
  videoId, 
  crossStart = 0, 
  currentVideoTime = 0,
  rawVideoData = null,
  onCrossStartChange 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const handleStartEdit = () => {
    // Start editing mode - pending value will update with video time
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!videoId) {
      setNotification({
        open: true,
        message: 'No video ID available',
        severity: 'error'
      });
      return;
    }

    if (!rawVideoData || !rawVideoData._version) {
      setNotification({
        open: true,
        message: 'Missing version data for update',
        severity: 'error'
      });
      return;
    }

    setIsUpdating(true);
    
    try {
      // Use current video time as the value to save
      const newCrossStart = currentVideoTime;
      
      // Update the database with version for optimistic locking
      const videoUpdates = {
        id: videoId,
        _version: rawVideoData._version,
        crossStart: newCrossStart
      };

      console.log('🔄 Updating crossStart in database:', videoUpdates);

      const result = await API.graphql({
        query: updateVideoMutation,
        variables: { input: videoUpdates }
      });

      console.log('✅ CrossStart update successful:', result);

      // Notify parent component of the change
      if (onCrossStartChange) {
        onCrossStartChange(newCrossStart);
      }

      setIsEditing(false);
      setNotification({
        open: true,
        message: `Cross start updated to ${newCrossStart.toFixed(1)}s`,
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Failed to update cross start:', error);
      setNotification({
        open: true,
        message: `Failed to update cross start: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
          CrossStart: {currentVideoTime.toFixed(1)}s
        </Typography>
        <Tooltip title="Save new cross start time">
          <IconButton 
            size="small" 
            onClick={handleSave}
            disabled={isUpdating}
            color="primary"
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel">
          <IconButton 
            size="small" 
            onClick={handleCancel}
            disabled={isUpdating}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Snackbar
          open={notification.open}
          autoHideDuration={3000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" color="secondary" sx={{ fontWeight: 'bold' }}>
        CrossStart: {crossStart?.toFixed(1) || 0}s
      </Typography>
      <Tooltip title={`Set cross start to current video time (${currentVideoTime.toFixed(1)}s)`}>
        <IconButton 
          size="small" 
          onClick={handleStartEdit}
          sx={{ ml: 0.5 }}
          color={Math.abs(crossStart - currentVideoTime) > 1 ? "primary" : "default"}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CrossStartSetter; 